/* eslint-disable max-len */

import React, { useState, useCallback } from "react";
import { Grid, InputAdornment, ClickAwayListener } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import PropTypes from "prop-types";
import { format, isValid } from "date-fns";
import { InfoTitle } from "../../../../components/InfoTitle";
import { DirhamSymbol } from "../../../../components/DirhamSymbol";
import {
  Input,
  AutoSaveField as Field,
  DecimalNumberInput,
  InlineRadioGroup,
  DatePicker
} from "../../../../components/Form";
import { GroupLabel, InfoTitlesStyle, NonCashTitleStyle, InfoIconButton } from "./styled";
import {
  YEAR_MONTH_COUNT,
  ANNUAL_TURNOVER_MAX_LENGTH,
  PLACEHOLDER,
  linkedFields,
  dnfbpInfoContent
} from "./constants";
import { isNumeric } from "../../../../utils/validation";
import { ContextualDnfbpHelp } from "../../../../components/Notifications";
import { addNotification } from "../../../../components/Notification";
import { YesNoList } from "../../../../constants/options";
import { DATE_FORMAT } from "../../../../constants";
import { Box } from "@mui/system";

// Export these functions for testing
export const getTotalMonthlyCreditsValue = annualFinancialTurnover => {
  if (!isNumeric(annualFinancialTurnover)) {
    return null;
  }
  const calculation = parseFloat(annualFinancialTurnover) / YEAR_MONTH_COUNT;
  return Math.floor(calculation);
};

export const getTotalMonthlyCreditsText = monthlyCreditsValue => {
  return isNumeric(monthlyCreditsValue)
    ? `${monthlyCreditsValue.toLocaleString("en-US")} in Total Monthly Credits`
    : PLACEHOLDER;
};

export const getPercentValue = (values, name, totalMonthlyCreditsAED) => {
  const totalMonthlyCashAmountInPercent = Math.round(
    (values.totalMonthlyCashAmountInFigures * 100) / totalMonthlyCreditsAED
  );
  return name === linkedFields.totalMonthlyCashAmountInPercent.name
    ? totalMonthlyCashAmountInPercent
    : 100 - totalMonthlyCashAmountInPercent;
};

export const createChangeProspectHandler = (values, annualFinTurnoverAmtInAED) => (
  _,
  __,
  ___,
  errors
) => {
  const isDataValid = !Object.values(linkedFields).some(
    field => field.isFillByUser && (!values[field.name] || errors[field.name])
  );

  if (!isDataValid) {
    return {};
  }

  const totalMonthlyCreditsAED = getTotalMonthlyCreditsValue(annualFinTurnoverAmtInAED);
  const prospectFields = Object.values(linkedFields).reduce(
    (resultObject, { path, name, isFillByUser }) => ({
      ...resultObject,
      [path]: isFillByUser ? values[name] : getPercentValue(values, name, totalMonthlyCreditsAED)
    }),
    {}
  );

  return {
    ...prospectFields,
    "prospect.orgKYCDetails.anticipatedTransactionsDetails.totalMonthlyCreditsAED": totalMonthlyCreditsAED
  };
};

const commonInputProps = {
  endAdornment: (
    <InputAdornment position="end">
      <DirhamSymbol />
    </InputAdornment>
  )
};

// Extracted component to avoid defining function in JSX
const StyledInfoTitle = () => <InfoTitlesStyle component={NonCashTitleStyle} />;

export const CompanyAnticipatedTransactionComponent = props => {
  const { annualFinTurnoverAmtInAED, dnfbpField, values, hawalaHandler } = props;
  const [isLinkVisited, setIsLinkVisited] = useState(dnfbpField);
  const [open, setOpen] = useState(false);

  const handleShowNotification = useCallback(
    () => !isLinkVisited && addNotification(dnfbpInfoContent),
    [isLinkVisited]
  );

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
    setIsLinkVisited(true);
  };

  const changeDateProspectHandler = (_, value, path) =>
    isValid(value) && { [path]: format(value, DATE_FORMAT) };

  return (
    <React.Fragment>
      <Box>
        <GroupLabel>
          Is your Company Dealing in Designated Business Categories?
          <ClickAwayListener onClickAway={handleTooltipClose}>
            <ContextualDnfbpHelp
              title={
                <>
                  Dealing in precious metals/precious stones/real estate or any commercial and/or
                  financial transactions/ operations, on behalf of/for benefit of, our existing
                  and/or potential clients, business/trade/ professional counterparts, and/or their
                  beneficial owners
                </>
              }
              placement="right"
              isDisableHoverListener={true}
              onClose={handleTooltipClose}
              open={open}
            >
              <InfoIconButton onClick={handleTooltipOpen}>
                <InfoOutlined />
              </InfoIconButton>
            </ContextualDnfbpHelp>
          </ClickAwayListener>
        </GroupLabel>
        <Field
          name={"isCompanyDesignated"}
          path="prospect.orgKYCDetails.anticipatedTransactionsDetails.isCompanyDesignated"
          component={InlineRadioGroup}
          options={YesNoList}
          InputProps={{
            inputProps: { tabIndex: 0, label: "" }
          }}
          onClick={handleShowNotification}
          disabled={!isLinkVisited}
          exhaustiveDeps={isLinkVisited}
        />
      </Box>
      {/* Hawala Activity fields */}
      <Field
        name={"isCompanyInvovledInHawala"}
        path="prospect.orgKYCDetails.anticipatedTransactionsDetails.isCompanyInvovledInHawala"
        label="Is the customer involved in Hawala activity?"
        component={InlineRadioGroup}
        options={YesNoList}
        InputProps={{
          inputProps: { tabIndex: 0, label: "" }
        }}
        onChange={hawalaHandler}
      />
      {values.isCompanyInvovledInHawala === "Yes" && (
        <Field
          name={"isCompanyAuthorizedHawala"}
          path="prospect.orgKYCDetails.anticipatedTransactionsDetails.isCompanyAuthorizedHawala"
          label="Is it an Authorized Hawala Service Provider?"
          component={InlineRadioGroup}
          options={YesNoList}
          InputProps={{
            inputProps: { tabIndex: 0, label: "" }
          }}
        />
      )}
      {values.isCompanyAuthorizedHawala === "Yes" && (
        <Field
          name={"hawalaLicenseExpDate"}
          path="prospect.orgKYCDetails.anticipatedTransactionsDetails.hawalaLicenseExpDate"
          label="Hawala License Expiry Date"
          component={DatePicker}
          InputProps={{
            inputProps: { tabIndex: 0 }
          }}
          minDate={new Date()}
          changeProspect={changeDateProspectHandler}
        />
      )}
      <Field
        name="totalMonthlyCreditsAED"
        path="prospect.orgKYCDetails.anticipatedTransactionsDetails.totalMonthlyCreditsAED"
        component={Input}
        variant="outlined"
        autoComplete="off"
        disabled
        InputProps={{
          ...commonInputProps,
          inputProps: {
            tabIndex: -1
          }
        }}
        infoTitle="This section is calculated based on the company's Annual Financial Turnover"
        label={getTotalMonthlyCreditsText(getTotalMonthlyCreditsValue(annualFinTurnoverAmtInAED))}
      />
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item sm={6} xs={12}>
          <Field
            name="totalMonthlyCashAmountInFigures"
            path="prospect.orgKYCDetails.anticipatedTransactionsDetails.totalMonthlyCashCreditsAED.amountInFigures"
            label="Monthly Total in Cash"
            placeholder={PLACEHOLDER}
            autoComplete="off"
            component={Input}
            contextualHelpText={
              <>
                Approximate amount that the company expects to receive in a month in Cash.
                <br />
                Enter 0 if there are no cash transactions.
              </>
            }
            InputProps={{
              ...commonInputProps,
              inputComponent: DecimalNumberInput,
              inputProps: { tabIndex: 0, maxLength: ANNUAL_TURNOVER_MAX_LENGTH }
            }}
            changeProspect={createChangeProspectHandler(values, annualFinTurnoverAmtInAED)}
          />
        </Grid>
        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            mt: {
              xs: 2,
              sm: 0
            }
          }}
        >
          <Field
            name="totalMonthlyNonCashAmountInFigures"
            autoComplete="off"
            path="prospect.orgKYCDetails.anticipatedTransactionsDetails.totalMonthlyNonCashCreditsAED.amountInFigures"
            label="Monthly Total in Non-Cash"
            placeholder={PLACEHOLDER}
            component={Input}
            contextualHelpText={
              <>
                Approximate amount that the company expects to receive in a month in modes other
                than Cash.
                <br />
                Enter 0 if there are no non-cash transactions.
              </>
            }
            InputProps={{
              ...commonInputProps,
              inputComponent: DecimalNumberInput,
              inputProps: { tabIndex: 0, maxLength: ANNUAL_TURNOVER_MAX_LENGTH }
            }}
            changeProspect={createChangeProspectHandler(values, annualFinTurnoverAmtInAED)}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 0 }}>
        <Grid item sm={6} xs={12}>
          <Field
            name="maxAmtSingleTxnCashAED"
            label="Maximum Amount in Cash"
            autoComplete="off"
            path="prospect.orgKYCDetails.anticipatedTransactionsDetails.maxAmtSingleTxnCashAED"
            placeholder={PLACEHOLDER}
            InputProps={{
              ...commonInputProps,
              inputComponent: DecimalNumberInput,
              inputProps: { tabIndex: 0, maxLength: ANNUAL_TURNOVER_MAX_LENGTH }
            }}
            component={Input}
            contextualHelpText={
              <>
                Approximate amount that the company expects to receive in single transaction in
                Cash.
                <br />
                Enter 0 if there are no cash transactions.
              </>
            }
          />
        </Grid>
        <Grid
          item
          sm={6}
          xs={12}
          sx={{
            mt: {
              xs: 2,
              sm: 0
            }
          }}
        >
          <Field
            name="maxAmtSingleTxnNonCashAED"
            label="Maximum Amount in Non-Cash"
            autoComplete="off"
            path="prospect.orgKYCDetails.anticipatedTransactionsDetails.maxAmtSingleTxnNonCashAED"
            placeholder={PLACEHOLDER}
            InputProps={{
              ...commonInputProps,
              inputComponent: DecimalNumberInput,
              inputProps: { tabIndex: 0, maxLength: ANNUAL_TURNOVER_MAX_LENGTH }
            }}
            component={Input}
            contextualHelpText={
              <>
                Approximate amount that the company expects to receive in single transaction in
                modes other than Cash.
                <br />
                Enter 0 if there are no non-cash transactions.
              </>
            }
          />
        </Grid>
        <Grid item sm={12} xs={12} sx={{ mt: 0.5 }}>
          <InfoTitle
            component={StyledInfoTitle}
            title="Non-cash: Cheque / EFT / Internal transfer / point of sale"
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

CompanyAnticipatedTransactionComponent.propTypes = {
  annualFinTurnoverAmtInAED: PropTypes.number.isRequired,
  dnfbpField: PropTypes.bool.isRequired,
  values: PropTypes.object.isRequired,
  hawalaHandler: PropTypes.func.isRequired
};
